/* Position the existing OCS widget around the main product's purchase controls. */
(() => {
  'use strict';

  if (!/\/products\/[^/]+\/?$/.test(window.location.pathname) || window.lpOcsPlacement) return;

  const EXCLUDED = 'quick-add-modal, .quick-add-modal, .mw-tingle, .complementary-products, .product-card-wrapper, sticky-add-to-cart';
  const HOST = '[id="edd-embed-host"]';
  const controllers = new Map();
  let rememberedHost = null;
  let rememberedProduct = '';

  function visible(element) {
    if (!element || element.closest('[hidden], [aria-hidden="true"], ' + EXCLUDED)) return false;
    if (!element.getClientRects().length) return false;
    for (let node = element; node instanceof Element; node = node.parentElement) {
      const style = window.getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
    }
    return true;
  }

  function attach(root) {
    if (controllers.has(root) || root.closest(EXCLUDED)) return;
    let host = null;
    let row = null;
    let frame = null;
    let disposed = false;
    let descriptionShadow = null;
    const descriptionObserver = new MutationObserver(updateDescriptionStyle);

    function updateDescriptionStyle() {
      if (disposed) return;
      const widget = [...document.querySelectorAll(HOST)].find(element => !element.closest(EXCLUDED));
      const shadow = widget?.shadowRoot;
      if (!shadow) return;
      const selector = 'style[data-lp-ocs-hide-description]';
      const portrait = root.dataset.lpBenefitsType === '2d' || root.dataset.lpBenefitsType === '3d' || root.dataset.lpBenefitsType === 'gift';
      if (!portrait) {
        shadow.querySelector(selector)?.remove();
        return;
      }
      if (descriptionShadow !== shadow) {
        descriptionObserver.disconnect();
        descriptionShadow = shadow;
        // Observe only direct children: OCS's per-second countdown updates
        // inside .desc do not need to run this style check.
        descriptionObserver.observe(shadow, { childList: true });
      }
      if (!shadow.querySelector(selector)) {
        const style = document.createElement('style');
        style.setAttribute('data-lp-ocs-hide-description', '');
        style.textContent = '.root > .desc { display: none !important; } .root > .card { margin-top: 0 !important; }';
        shadow.append(style);
      }
    }

    function releaseRow() {
      if (row) row.removeAttribute('data-lp-ocs-purchase-row');
      row = null;
    }

    function place() {
      frame = null;
      if (disposed || !root.isConnected) return;
      const info = root.querySelector('.product__info-container');
      if (!info) return;
      updateDescriptionStyle();

      // Gift PDPs have no APO step row; place the timeline after the
      // purchase buttons and above the accordion group.
      if (root.dataset.lpBenefitsType === 'gift') {
        const apo = info.querySelector('.mw-apo-container');
        const add = info.querySelector('.mw-apo-add-to-cart-custom');
        const checkout = info.querySelector('.lp-direct-checkout');
        if (!apo || !apo.parentElement || !add || !visible(apo) || !visible(add) || !checkout || !checkout.parentElement) return;

        const giftProduct = info.dataset.url || window.location.pathname;
        const discoveredHost = [...document.querySelectorAll(HOST)].find(element => !element.closest(EXCLUDED));
        if (discoveredHost) host = discoveredHost;
        else if (!host && rememberedProduct === giftProduct) host = rememberedHost;
        if (!host || !host.isConnected || host === apo || host.contains(apo)) return;
        rememberedHost = host;
        rememberedProduct = giftProduct;

        if (host.parentElement !== checkout.parentElement || host.previousElementSibling !== checkout) {
          checkout.parentElement.insertBefore(host, checkout.nextSibling);
        }
        return;
      }

      // On the final APO step the direct-checkout block is mounted after the
      // step row. Keep the timeline inside that block so it follows "More
      // payment options"; the mobile benefits block anchors directly after
      // the same block, so a sibling timeline would be pushed back and forth.
      const benefitsType = root.dataset.lpBenefitsType;
      if (benefitsType === '2d' || benefitsType === '3d') {
        const checkout = info.querySelector('.lp-direct-checkout');
        if (checkout && visible(checkout)) {
          const product = info.dataset.url || window.location.pathname;
          const discovered = [...document.querySelectorAll(HOST)].find(element => !element.closest(EXCLUDED));
          if (discovered) host = discovered;
          else if (!host && rememberedProduct === product) host = rememberedHost;
          if (!host || host.contains(checkout)) return;
          rememberedHost = host;
          rememberedProduct = product;
          releaseRow();
          if (host.hasAttribute('data-lp-ocs-above-step')) host.removeAttribute('data-lp-ocs-above-step');
          if (host.parentElement !== checkout || checkout.lastElementChild !== host) checkout.append(host);
          return;
        }
      }

      const target = [...info.querySelectorAll('.mw-group__button')].find(element =>
        !element.closest(EXCLUDED) && element.querySelector('.mw-group__button-next') && visible(element)
      );
      // Products without APO step controls keep OCS's own placement.
      if (!target) return;

      const product = info.dataset.url || window.location.pathname;
      const discovered = [...document.querySelectorAll(HOST)].find(element => !element.closest(EXCLUDED));
      if (discovered) host = discovered;
      else if (!host && rememberedProduct === product) host = rememberedHost;
      if (!host || host.contains(target)) return;
      rememberedHost = host;
      rememberedProduct = product;

      if (row !== target) {
        releaseRow();
        row = target;
        row.setAttribute('data-lp-ocs-purchase-row', '');
      }
      if (!host.hasAttribute('data-lp-ocs-above-step')) host.setAttribute('data-lp-ocs-above-step', '');
      // Move the original node, including its shadow root and live countdown.
      // Keeping it inside the row preserves price/discount sibling anchors.
      // 2D/3D place the timeline after the step buttons, before the benefits
      // block and accordions; other products keep it above the row.
      const belowStep = benefitsType === '2d' || benefitsType === '3d';
      const placed = belowStep ? row.lastElementChild === host : row.firstElementChild === host;
      if (host.parentElement !== row || !placed) {
        if (belowStep) row.append(host);
        else row.prepend(host);
      }
    }

    function schedule() {
      if (disposed || frame !== null) return;
      frame = window.requestAnimationFrame(place);
    }

    const observer = new MutationObserver(schedule);
    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'aria-hidden']
    });

    const controller = {
      schedule,
      destroy() {
        disposed = true;
        observer.disconnect();
        descriptionObserver.disconnect();
        if (frame !== null) window.cancelAnimationFrame(frame);
        releaseRow();
        if (host) host.removeAttribute('data-lp-ocs-above-step');
        controllers.delete(root);
      }
    };
    controllers.set(root, controller);
    schedule();
  }

  function initialize() {
    for (const [root, controller] of controllers) {
      if (!root.isConnected) controller.destroy();
    }
    document.querySelectorAll('main [id^="MainProduct-"]').forEach(attach);
    controllers.forEach(controller => controller.schedule());
  }

  // OCS initially emits its host outside the product and moves it after its
  // request completes. React only to host/product insertion or replacement.
  const discovery = new MutationObserver(records => {
    const relevant = records.some(record => [...record.addedNodes, ...record.removedNodes].some(node =>
      node instanceof Element && (node.matches(HOST + ', [id^="MainProduct-"]') ||
        node.querySelector(HOST + ', [id^="MainProduct-"]'))
    ));
    if (relevant) initialize();
  });

  function start() {
    initialize();
    discovery.observe(document.body, { childList: true, subtree: true });
  }

  window.lpOcsPlacement = { refresh: initialize };
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, controller] of controllers) {
      if (event.target.contains(root)) controller.destroy();
    }
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
